import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import colors from 'constants/colors'

/**
 * The extra props this HOC adds vary in shape depending on the wrapped modal, so they
 * are modelled as a loose record with the fields this file itself relies on made explicit.
 */
type ConfirmationModalProps = {
  open?: boolean
  onClose?(value?: unknown): void
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  fullWidth?: boolean
  extraButtonAction?: () => void
  extraButtonLabel?: string
  data?: unknown
  noLabel?: string
  yesLabel?: string
  confirm?: () => void
  [key: string]: unknown
}

/**
 * Confirmation modal higher order component.
 *
 * @param ModalComponent - Modal component to wrap.
 * @returns Confirmation modal component.
 * @notExported
 */
const WithConfirmationModal = <TModalProps extends object>(ModalComponent: React.ComponentType<TModalProps>) => {
  const ConfirmationModal = (props: ConfirmationModalProps) => {
    const { t } = useTranslation()
    const [disableDelete, setDisableDelete] = useState<boolean>(false)

    return (
      <>
        {props.open && (
          <Dialog
            open
            onClose={() => props.onClose?.({})}
            maxWidth={props.maxWidth}
            fullWidth={props.fullWidth}
            aria-labelledby="new-confirmation-dialog-title"
            sx={{
              '.MuiDialog-paper': {
                backgroundColor: colors.secondaryBackground,
                borderRadius: 1,
              },
            }}
          >
            <DialogTitle
              id="new-confirmation-dialog-title"
              sx={{
                position: 'absolute',
                width: 1,
                height: 1,
                overflow: 'hidden',
                clip: 'rect(0,0,0,0)',
                whiteSpace: 'nowrap',
              }}
            >
              {t('confirmation')}
            </DialogTitle>
            {React.createElement(ModalComponent, {
              ...props,
              disableDelete: setDisableDelete,
            } as unknown as React.Attributes & TModalProps)}
            <DialogActions
              sx={{
                background: colors.secondaryBackground,
                paddingRight: '22px',
                paddingBottom: '22px',
              }}
            >
              {props.extraButtonAction && props.extraButtonLabel && (
                <Button
                  onClick={() => props.extraButtonAction?.()}
                  data-testid="button-confirmation-extra"
                  disabled={!props.data || props.data === null}
                  variant="contained"
                  color="error"
                >
                  {props.extraButtonLabel}
                </Button>
              )}
              <Button
                data-testid="button-confirmation-close"
                onClick={props.onClose}
                variant="contained"
                color="primary"
              >
                {props.noLabel ? t(props.noLabel) : !disableDelete ? t('confirmation.no') : t('confirmation.ok')}
              </Button>
              {!disableDelete && (
                <Button
                  data-testid="button-confirmation-confirm"
                  onClick={props.confirm}
                  variant="contained"
                  color="primary"
                >
                  {props.yesLabel ? t(props.yesLabel) : t('confirmation.yes')}
                </Button>
              )}
            </DialogActions>
          </Dialog>
        )}
      </>
    )
  }
  return ConfirmationModal
}

export default WithConfirmationModal
